'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { fetchCalendarEvents, fetchCalendarMeta, fetchUpcomingCalendarEvents } from './api';
import { CalendarEventModal } from './CalendarEventModal';
import { CalendarSidebarEventLink } from './CalendarSidebarEventLink';
import { draftFromEvent, pad2, toDateInput } from './eventUtils';
import type { CalendarEvent, CalendarMeta, SidebarScope } from './types';
import './calendar.css';

function monthRange(year: number, month: number) {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

function buildMiniMonth(year: number, month: number, eventDays: Set<string>) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { date: string; day: number; inMonth: boolean; hasEvents: boolean }[] = [];
  for (let i = 0; i < startPad; i++) {
    cells.push({ date: '', day: 0, inMonth: false, hasEvents: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${pad2(month + 1)}-${pad2(d)}`;
    cells.push({ date, day: d, inMonth: true, hasEvents: eventDays.has(date) });
  }
  return cells;
}

const FULL_WIDTH_PREFIXES = [
  '/calendar',
  '/admin',
  '/claims',
  '/login',
  '/legal',
  '/marketplace/checkout',
];

function isFullWidthRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return FULL_WIDTH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function shouldShowCalendarSidebar(scope: SidebarScope, pathname: string): boolean {
  if (scope === 'off') return false;
  if (isFullWidthRoute(pathname)) return false;
  const path = pathname.replace(/\/+$/, '') || '/';
  if (scope === 'home') return path === '/';
  return true;
}

type DiscoveryRightRailProps = {
  right?: ReactNode;
};

/** Right rail: optional page content + calendar sidebar when admin scope allows. */
export function DiscoveryRightRail({ right }: DiscoveryRightRailProps) {
  const pathname = usePathname();
  const [meta, setMeta] = useState<CalendarMeta | null>(null);
  const [metaLoaded, setMetaLoaded] = useState(false);

  useEffect(() => {
    fetchCalendarMeta()
      .then(setMeta)
      .catch(() => setMeta(null))
      .finally(() => setMetaLoaded(true));
  }, []);

  const showCalendar = meta && shouldShowCalendarSidebar(meta.sidebarScope, pathname);
  if (metaLoaded && !right && !showCalendar) return null;
  if (!metaLoaded && !right) return null;

  return (
    <aside className="discovery-layout__right">
      {showCalendar && meta && <CalendarSidebarWidget meta={meta} calendarPath="/calendar/" />}
      {right}
    </aside>
  );
}

type WidgetProps = {
  meta: CalendarMeta;
  calendarPath?: string;
};

export function CalendarSidebarWidget({ meta, calendarPath = '/calendar/' }: WidgetProps) {
  const { isAuthenticated } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [upcoming, setUpcoming] = useState<CalendarEvent[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestMode, setRequestMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [initialDraft, setInitialDraft] = useState<ReturnType<typeof draftFromEvent> | undefined>();

  const load = useCallback(() => {
    const { from, to } = monthRange(year, month);
    fetchCalendarEvents(from, to).then((res) => setEvents(res.events ?? [])).catch(() => setEvents([]));
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchUpcomingCalendarEvents(10)
      .then((res) => setUpcoming(res.events ?? []))
      .catch(() => setUpcoming([]));
  }, []);

  const eventDays = useMemo(() => {
    const days = new Set<string>();
    for (const e of events) {
      days.add(e.startAt.slice(0, 10));
    }
    return days;
  }, [events]);

  const cells = useMemo(() => buildMiniMonth(year, month, eventDays), [year, month, eventDays]);

  const todayKey = toDateInput(now);

  function openDay(date: string) {
    if (!date) return;
    if (meta.canEdit) {
      setRequestMode(false);
      setSelectedEvent(null);
      setInitialDraft({ ...draftFromEvent(null), startDate: date, endDate: date, allDay: true });
      setModalOpen(true);
      return;
    }
    if (meta.canSubmitEvent) {
      setRequestMode(true);
      setSelectedEvent(null);
      setInitialDraft({ ...draftFromEvent(null), startDate: date, endDate: date, allDay: true });
      setModalOpen(true);
    }
  }

  function refreshAll() {
    load();
    fetchUpcomingCalendarEvents(10)
      .then((res) => setUpcoming(res.events ?? []))
      .catch(() => setUpcoming([]));
  }

  return (
    <div className="rail-card calendar-sidebar">
      <div className="calendar-sidebar__header">
        <h4>{meta.title}</h4>
        {meta.canEdit && (
          <button
            type="button"
            className="calendar-sidebar__create"
            aria-label="Create event"
            onClick={() => {
              setRequestMode(false);
              setSelectedEvent(null);
              setInitialDraft(undefined);
              setModalOpen(true);
            }}
          >
            +
          </button>
        )}
        {meta.canSubmitEvent && !meta.canEdit && (
          <button
            type="button"
            className="calendar-sidebar__create"
            aria-label="Suggest event"
            onClick={() => {
              setRequestMode(true);
              setSelectedEvent(null);
              setInitialDraft(undefined);
              setModalOpen(true);
            }}
          >
            +
          </button>
        )}
      </div>
      <div className="calendar-sidebar__month-nav">
        <button type="button" aria-label="Previous month" onClick={() => {
          if (month === 0) {
            setMonth(11);
            setYear((y) => y - 1);
          } else setMonth((m) => m - 1);
        }}>
          ‹
        </button>
        <span>{new Date(year, month, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' })}</span>
        <button type="button" aria-label="Next month" onClick={() => {
          if (month === 11) {
            setMonth(0);
            setYear((y) => y + 1);
          } else setMonth((m) => m + 1);
        }}>
          ›
        </button>
      </div>
      <div className="calendar-sidebar__weekdays">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="calendar-sidebar__grid">
        {cells.map((cell, i) => (
          <button
            key={`${cell.date}-${i}`}
            type="button"
            className={`calendar-sidebar__day${cell.date === todayKey ? ' calendar-sidebar__day--today' : ''}${cell.hasEvents ? ' calendar-sidebar__day--has-events' : ''}${cell.inMonth ? '' : ' calendar-sidebar__day--pad'}`}
            disabled={!cell.inMonth}
            onClick={() => openDay(cell.date)}
          >
            {cell.inMonth ? cell.day : ''}
          </button>
        ))}
      </div>
      {upcoming.length > 0 && (
        <>
          <h5 className="calendar-sidebar__upcoming-heading">Upcoming</h5>
          <ul className="calendar-sidebar__upcoming">
            {upcoming.map((e) => (
              <li key={e.id}>
                <CalendarSidebarEventLink event={e} calendarPath={calendarPath} />
              </li>
            ))}
          </ul>
        </>
      )}
      <Link href={calendarPath} className="calendar-sidebar__open">
        Open calendar
      </Link>
      <CalendarEventModal
        open={modalOpen}
        event={selectedEvent}
        canEdit={meta.canEdit}
        requestMode={requestMode}
        isAuthenticated={isAuthenticated}
        initialDraft={initialDraft}
        onClose={() => setModalOpen(false)}
        onSaved={refreshAll}
      />
    </div>
  );
}